/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxTreeView from 'devextreme/ui/tree_view';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiItemModule } from './nested/item-dxi';
import { DxoSearchEditorOptionsModule } from './nested/search-editor-options';

import { DxiItemComponent } from './nested/item-dxi';




/**
 * The TreeView widget is a tree-like representation of textual data.
 */

export class DxTreeViewComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxTreeView;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the widget changes its state when interacting with a user.
     */
    
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies whether or not to animate item collapsing and expanding.
     */
    
    get animationEnabled(): boolean {
        return this._getOption('animationEnabled');
    }
    set animationEnabled(value: boolean) {
        this._setOption('animationEnabled', value);
    }


    /**
     * Allows you to load nodes manually.
     */
    
    get createChildren(): Function {
        return this._getOption('createChildren');
    }
    set createChildren(value: Function) {
        this._setOption('createChildren', value);
    }


    /**
     * A data source used to fetch data to be displayed by the widget.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether a nested or plain array is used as a data source.
     */
    
    get dataStructure(): string {
        return this._getOption('dataStructure');
    }
    set dataStructure(value: string) {
        this._setOption('dataStructure', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding widget item is disabled.
     */
    
    get disabledExpr(): Function|
        string {
        return this._getOption('disabledExpr');
    }
    set disabledExpr(value: Function|
        string) {
        this._setOption('disabledExpr', value);
    }


    /**
     * Specifies the name of the data source item field whose value is displayed by the widget.
     */
    
    get displayExpr(): Function|
        string {
        return this._getOption('displayExpr');
    }
    set displayExpr(value: Function|
        string) {
        this._setOption('displayExpr', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether or not a user can expand all tree view items by the "*" hot key.
     */
    
    get expandAllEnabled(): boolean {
        return this._getOption('expandAllEnabled');
    }
    set expandAllEnabled(value: boolean) {
        this._setOption('expandAllEnabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding widget item is displayed expanded.
     */
    
    get expandedExpr(): Function|
        string {
        return this._getOption('expandedExpr');
    }
    set expandedExpr(value: Function|
        string) {
        this._setOption('expandedExpr', value);
    }


    /**
     * Specifies whether or not all parent nodes of an initially expanded node are displayed expanded.
     */
    
    get expandNodesRecursive(): boolean {
        return this._getOption('expandNodesRecursive');
    }
    set expandNodesRecursive(value: boolean) {
        this._setOption('expandNodesRecursive', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding node includes child nodes.
     */
    
    get hasItemsExpr(): Function|
        string {
        return this._getOption('hasItemsExpr');
    }
    set hasItemsExpr(value: Function|
        string) {
        this._setOption('hasItemsExpr', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * The time period in milliseconds before the onItemHold event is raised.
     */
    
    get itemHoldTimeout(): number {
        return this._getOption('itemHoldTimeout');
    }
    set itemHoldTimeout(value: number) {
        this._setOption('itemHoldTimeout', value);
    }


    
    get items(): any|
        Array<any> {
        return this._getOption('items');
    }
    set items(value: any|
        Array<any>) {
        this._setOption('items', value);
    }


    /**
     * Specifies which data field contains nested items.
     */
    
    get itemsExpr(): Function|
        string {
        return this._getOption('itemsExpr');
    }
    set itemsExpr(value: Function|
        string) {
        this._setOption('itemsExpr', value);
    }


    /**
     * Specifies a custom template for an item.
     */
    
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * Specifies which data field provides keys for TreeView items.
     */
    
    get keyExpr(): Function|
        string {
        return this._getOption('keyExpr');
    }
    set keyExpr(value: Function|
        string) {
        this._setOption('keyExpr', value);
    }


    /**
     * The text or HTML markup displayed by the widget if the item collection is empty.
     */
    
    get noDataText(): string {
        return this._getOption('noDataText');
    }
    set noDataText(value: string) {
        this._setOption('noDataText', value);
    }


    /**
     * Specifies the name of the data source item field for holding the parent key of the corresponding node.
     */
    
    get parentIdExpr(): Function|
        string {
        return this._getOption('parentIdExpr');
    }
    set parentIdExpr(value: Function|
        string) {
        this._setOption('parentIdExpr', value);
    }


    /**
     * Specifies the parent ID value of the root item.
     */
    
    get rootValue(): any {
        return this._getOption('rootValue');
    }
    set rootValue(value: any) {
        this._setOption('rootValue', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A string value specifying available scrolling directions.
     */
    
    get scrollDirection(): string {
        return this._getOption('scrollDirection');
    }
    set scrollDirection(value: string) {
        this._setOption('scrollDirection', value);
    }


    /**
     * Configures the search panel.
     */
    
    get searchEditorOptions(): DevExpress.ui.dxTextBoxOptions {
        return this._getOption('searchEditorOptions');
    }
    set searchEditorOptions(value: DevExpress.ui.dxTextBoxOptions) {
        this._setOption('searchEditorOptions', value);
    }


    /**
     * Specifies whether the search panel is visible.
     */
    
    get searchEnabled(): boolean {
        return this._getOption('searchEnabled');
    }
    set searchEnabled(value: boolean) {
        this._setOption('searchEnabled', value);
    }


    /**
     * Specifies a data object's field name or an expression whose value is compared to the search string.
     */
    
    get searchExpr(): Function|
        Array<Function> {
        return this._getOption('searchExpr');
    }
    set searchExpr(value: Function|
        Array<Function>) {
        this._setOption('searchExpr', value);
    }


    /**
     * Specifies whether the widget finds entries that contain your search string or entries that only start with it.
     */
    
    get searchMode(): string {
        return this._getOption('searchMode');
    }
    set searchMode(value: string) {
        this._setOption('searchMode', value);
    }


    /**
     * Specifies the current search string.
     */
    
    get searchValue(): string {
        return this._getOption('searchValue');
    }
    set searchValue(value: string) {
        this._setOption('searchValue', value);
    }


    /**
     * Use the showCheckBoxesMode options instead.
     */
    
    get selectAllEnabled(): boolean {
        return this._getOption('selectAllEnabled');
    }
    set selectAllEnabled(value: boolean) {
        this._setOption('selectAllEnabled', value);
    }


    /**
     * Specifies the text displayed at the "Select All" check box.
     */
    
    get selectAllText(): string {
        return this._getOption('selectAllText');
    }
    set selectAllText(value: string) {
        this._setOption('selectAllText', value);
    }


    /**
     * Specifies whether or not an item becomes selected if a user clicks it.
     */
    
    get selectByClick(): boolean {
        return this._getOption('selectByClick');
    }
    set selectByClick(value: boolean) {
        this._setOption('selectByClick', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding widget items is selected.
     */
    
    get selectedExpr(): Function|
        string {
        return this._getOption('selectedExpr');
    }
    set selectedExpr(value: Function|
        string) {
        this._setOption('selectedExpr', value);
    }


    /**
     * Specifies item selection mode.
     */
    
    get selectionMode(): string {
        return this._getOption('selectionMode');
    }
    set selectionMode(value: string) {
        this._setOption('selectionMode', value);
    }


    /**
     * Specifies whether or not to select nodes recursively.
     */
    
    get selectNodesRecursive(): boolean {
        return this._getOption('selectNodesRecursive');
    }
    set selectNodesRecursive(value: boolean) {
        this._setOption('selectNodesRecursive', value);
    }


    /**
     * Use the showCheckBoxesMode options instead.
     */
    
    get showCheckBoxes(): boolean {
        return this._getOption('showCheckBoxes');
    }
    set showCheckBoxes(value: boolean) {
        this._setOption('showCheckBoxes', value);
    }


    /**
     * Specifies the current check boxes display mode.
     */
    
    get showCheckBoxesMode(): string {
        return this._getOption('showCheckBoxesMode');
    }
    set showCheckBoxesMode(value: string) {
        this._setOption('showCheckBoxesMode', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies if the virtual mode is enabled.
     */
    
    get virtualModeEnabled(): boolean {
        return this._getOption('virtualModeEnabled');
    }
    set virtualModeEnabled(value: boolean) {
        this._setOption('virtualModeEnabled', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the itemClick event.
     */
     onItemClick: EventEmitter<any>;

    /**
     * A handler for the itemCollapsed event.
     */
     onItemCollapsed: EventEmitter<any>;

    /**
     * A handler for the itemContextMenu event.
     */
     onItemContextMenu: EventEmitter<any>;

    /**
     * A handler for the itemExpanded event.
     */
     onItemExpanded: EventEmitter<any>;

    /**
     * A handler for the itemHold event.
     */
     onItemHold: EventEmitter<any>;

    /**
     * A handler for the itemRendered event.
     */
     onItemRendered: EventEmitter<any>;

    /**
     * Use the itemSelectionChanged event instead.
     */
     onItemSelected: EventEmitter<any>;

    /**
     * A handler for the itemSelectionChanged event.
     */
     onItemSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the selectionChanged event. Raised after an item is selected or unselected.
     */
     onSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
     accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
     activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the animationEnabledChange event.
     */
     animationEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the createChildrenChange event.
     */
     createChildrenChange: EventEmitter<Function>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>>;

    /**
     * A handler for the dataStructureChange event.
     */
     dataStructureChange: EventEmitter<string>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the disabledExprChange event.
     */
     disabledExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the displayExprChange event.
     */
     displayExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the expandAllEnabledChange event.
     */
     expandAllEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the expandedExprChange event.
     */
     expandedExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the expandNodesRecursiveChange event.
     */
     expandNodesRecursiveChange: EventEmitter<boolean>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the hasItemsExprChange event.
     */
     hasItemsExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the itemHoldTimeoutChange event.
     */
     itemHoldTimeoutChange: EventEmitter<number>;

    /**
     * A handler for the itemsChange event.
     */
     itemsChange: EventEmitter<any|
        Array<any>>;

    /**
     * A handler for the itemsExprChange event.
     */
     itemsExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the itemTemplateChange event.
     */
     itemTemplateChange: EventEmitter<any>;

    /**
     * A handler for the keyExprChange event.
     */
     keyExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the noDataTextChange event.
     */
     noDataTextChange: EventEmitter<string>;

    /**
     * A handler for the parentIdExprChange event.
     */
     parentIdExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the rootValueChange event.
     */
     rootValueChange: EventEmitter<any>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollDirectionChange event.
     */
     scrollDirectionChange: EventEmitter<string>;

    /**
     * A handler for the searchEditorOptionsChange event.
     */
     searchEditorOptionsChange: EventEmitter<DevExpress.ui.dxTextBoxOptions>;

    /**
     * A handler for the searchEnabledChange event.
     */
     searchEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the searchExprChange event.
     */
     searchExprChange: EventEmitter<Function|
        Array<Function>>;

    /**
     * A handler for the searchModeChange event.
     */
     searchModeChange: EventEmitter<string>;

    /**
     * A handler for the searchValueChange event.
     */
     searchValueChange: EventEmitter<string>;

    /**
     * A handler for the selectAllEnabledChange event.
     */
     selectAllEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the selectAllTextChange event.
     */
     selectAllTextChange: EventEmitter<string>;

    /**
     * A handler for the selectByClickChange event.
     */
     selectByClickChange: EventEmitter<boolean>;

    /**
     * A handler for the selectedExprChange event.
     */
     selectedExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the selectionModeChange event.
     */
     selectionModeChange: EventEmitter<string>;

    /**
     * A handler for the selectNodesRecursiveChange event.
     */
     selectNodesRecursiveChange: EventEmitter<boolean>;

    /**
     * A handler for the showCheckBoxesChange event.
     */
     showCheckBoxesChange: EventEmitter<boolean>;

    /**
     * A handler for the showCheckBoxesModeChange event.
     */
     showCheckBoxesModeChange: EventEmitter<string>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the virtualModeEnabledChange event.
     */
     virtualModeEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;




    
    get itemsChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this.setChildren('items', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemCollapsed', emit: 'onItemCollapsed' },
            { subscribe: 'itemContextMenu', emit: 'onItemContextMenu' },
            { subscribe: 'itemExpanded', emit: 'onItemExpanded' },
            { subscribe: 'itemHold', emit: 'onItemHold' },
            { subscribe: 'itemRendered', emit: 'onItemRendered' },
            { subscribe: 'itemSelected', emit: 'onItemSelected' },
            { subscribe: 'itemSelectionChanged', emit: 'onItemSelectionChanged' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'animationEnabledChange' },
            { emit: 'createChildrenChange' },
            { emit: 'dataSourceChange' },
            { emit: 'dataStructureChange' },
            { emit: 'disabledChange' },
            { emit: 'disabledExprChange' },
            { emit: 'displayExprChange' },
            { emit: 'elementAttrChange' },
            { emit: 'expandAllEnabledChange' },
            { emit: 'expandedExprChange' },
            { emit: 'expandNodesRecursiveChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'hasItemsExprChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemHoldTimeoutChange' },
            { emit: 'itemsChange' },
            { emit: 'itemsExprChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'keyExprChange' },
            { emit: 'noDataTextChange' },
            { emit: 'parentIdExprChange' },
            { emit: 'rootValueChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollDirectionChange' },
            { emit: 'searchEditorOptionsChange' },
            { emit: 'searchEnabledChange' },
            { emit: 'searchExprChange' },
            { emit: 'searchModeChange' },
            { emit: 'searchValueChange' },
            { emit: 'selectAllEnabledChange' },
            { emit: 'selectAllTextChange' },
            { emit: 'selectByClickChange' },
            { emit: 'selectedExprChange' },
            { emit: 'selectionModeChange' },
            { emit: 'selectNodesRecursiveChange' },
            { emit: 'showCheckBoxesChange' },
            { emit: 'showCheckBoxesModeChange' },
            { emit: 'tabIndexChange' },
            { emit: 'virtualModeEnabledChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxTreeView(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
        this.setupChanges('searchExpr', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._idh.doCheck('searchExpr');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-tree-view',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'accessKey': [{ type: Input },],
'activeStateEnabled': [{ type: Input },],
'animationEnabled': [{ type: Input },],
'createChildren': [{ type: Input },],
'dataSource': [{ type: Input },],
'dataStructure': [{ type: Input },],
'disabled': [{ type: Input },],
'disabledExpr': [{ type: Input },],
'displayExpr': [{ type: Input },],
'elementAttr': [{ type: Input },],
'expandAllEnabled': [{ type: Input },],
'expandedExpr': [{ type: Input },],
'expandNodesRecursive': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'hasItemsExpr': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'itemHoldTimeout': [{ type: Input },],
'items': [{ type: Input },],
'itemsExpr': [{ type: Input },],
'itemTemplate': [{ type: Input },],
'keyExpr': [{ type: Input },],
'noDataText': [{ type: Input },],
'parentIdExpr': [{ type: Input },],
'rootValue': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'scrollDirection': [{ type: Input },],
'searchEditorOptions': [{ type: Input },],
'searchEnabled': [{ type: Input },],
'searchExpr': [{ type: Input },],
'searchMode': [{ type: Input },],
'searchValue': [{ type: Input },],
'selectAllEnabled': [{ type: Input },],
'selectAllText': [{ type: Input },],
'selectByClick': [{ type: Input },],
'selectedExpr': [{ type: Input },],
'selectionMode': [{ type: Input },],
'selectNodesRecursive': [{ type: Input },],
'showCheckBoxes': [{ type: Input },],
'showCheckBoxesMode': [{ type: Input },],
'tabIndex': [{ type: Input },],
'virtualModeEnabled': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onItemCollapsed': [{ type: Output },],
'onItemContextMenu': [{ type: Output },],
'onItemExpanded': [{ type: Output },],
'onItemHold': [{ type: Output },],
'onItemRendered': [{ type: Output },],
'onItemSelected': [{ type: Output },],
'onItemSelectionChanged': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onSelectionChanged': [{ type: Output },],
'accessKeyChange': [{ type: Output },],
'activeStateEnabledChange': [{ type: Output },],
'animationEnabledChange': [{ type: Output },],
'createChildrenChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'dataStructureChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'disabledExprChange': [{ type: Output },],
'displayExprChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'expandAllEnabledChange': [{ type: Output },],
'expandedExprChange': [{ type: Output },],
'expandNodesRecursiveChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'hasItemsExprChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'itemHoldTimeoutChange': [{ type: Output },],
'itemsChange': [{ type: Output },],
'itemsExprChange': [{ type: Output },],
'itemTemplateChange': [{ type: Output },],
'keyExprChange': [{ type: Output },],
'noDataTextChange': [{ type: Output },],
'parentIdExprChange': [{ type: Output },],
'rootValueChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'scrollDirectionChange': [{ type: Output },],
'searchEditorOptionsChange': [{ type: Output },],
'searchEnabledChange': [{ type: Output },],
'searchExprChange': [{ type: Output },],
'searchModeChange': [{ type: Output },],
'searchValueChange': [{ type: Output },],
'selectAllEnabledChange': [{ type: Output },],
'selectAllTextChange': [{ type: Output },],
'selectByClickChange': [{ type: Output },],
'selectedExprChange': [{ type: Output },],
'selectionModeChange': [{ type: Output },],
'selectNodesRecursiveChange': [{ type: Output },],
'showCheckBoxesChange': [{ type: Output },],
'showCheckBoxesModeChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'virtualModeEnabledChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'itemsChildren': [{ type: ContentChildren, args: [DxiItemComponent, ] },],
};
}


export class DxTreeViewModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxiItemModule,
    DxoSearchEditorOptionsModule,
    DxTemplateModule
  ],
  declarations: [
    DxTreeViewComponent
  ],
  exports: [
    DxTreeViewComponent,
    DxiItemModule,
    DxoSearchEditorOptionsModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
